import { useEffect, useState } from 'react';
import {
  Container,
  IconButton,
  makeStyles,
  Snackbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { string, object } from 'yup';
import { Edit, AddCircle } from '@material-ui/icons';

import SearchInput from '../tableElements/searchInput';
import Table from '../tableElements/table';
import ManagementModal from '../managementModal';
import Notification from 'src/components/ui/notification';

import { addCompany, editCompany, getCompanies } from 'src/state/entities/companies/actions';
import { getCompaniesData } from 'src/state/entities/companies/selectors';
import { internationalPhone } from 'src/helpers/validation';
import { useCurrent } from 'src/helpers/hooks';

import { Column } from '../tableElements/types';
import { Company } from './types';

import { primaryColor } from 'src/data/colors';

const useStyles = makeStyles((theme) => ({
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    padding: '20px 0 0',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  container: {
    padding: '20px',
    width: '100%',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  leftHeader: {
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    color: primaryColor,
    padding: '0 0 0 10px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

const columns: Column[] = [
  { id: 'id', label: 'Id' },
  { id: 'name', label: 'Name' },
  {
    id: 'address',
    label: 'Address',
  },
  {
    id: 'contactPhoneNumber',
    label: 'Contact Phone Number',
  },
  {
    id: 'contactEmail',
    label: 'Contact Email',
  },
  {
    action: true,
    id: 'actions',
    label: '',
  },
];

const validationSchema = object({
  name: string().min(0, 'Name is required.').required('Name is required.'),
  contactPhoneNumber: internationalPhone({
    message: 'Enter country code followed by phone number: +1-999-999-9999',
  }).nullable(),
  contactEmail: string()
    .email('Company should have a valid email address for the contact')
    .nullable(),
});

const Companies = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const companiesData = useSelector(getCompaniesData);
  const [searchValue, setSearchValue] = useState('');
  const [editData, setEditData] = useState<Company | null>(null);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [add, setAdd] = useState<boolean>(false);

  const action = useCurrent(getCompanies.request(1, searchValue));

  useEffect(() => {
    if (action.current && !companiesData.items.length) {
      dispatch(action.current);
    }
  }, [action, dispatch]);

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    dispatch(getCompanies.request(value, searchValue));
  };

  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);
    dispatch(getCompanies.request(1, searchText));
  };

  const handleEdit = (id: Number) => {
    setEditData(companiesData.items.find((company) => company.id === id) || null);
  };

  const handleClose = () => {
    setEditData(null);
    setAdd(false);
  };

  const actions = [
    {
      Icon: Edit,
      action: handleEdit,
      title: 'Edit',
    },
  ];

  const onSubmit = (values: Record<string, string | number>) => {
    const companyValues = values as unknown as Company;

    if (add) {
      dispatch(addCompany.request(companyValues));
      return;
    } else {
      dispatch(editCompany.request(companyValues.id, companyValues));
    }
  };

  useEffect(() => {
    if (!companiesData.saving && !companiesData.error) {
      setEditData(null);

      if (add) {
        setAdd(false);
        dispatch(getCompanies.request(1, searchValue));
      }
    }
  }, [companiesData.saving]);

  useEffect(() => {
    if (companiesData.error || companiesData.success) {
      setShowMessage(true);
    }
  }, [companiesData.error, companiesData.success]);

  return (
    <Container className={classes.container}>
      <Container className={classes.header}>
        <Container className={classes.leftHeader}>
          <Typography className={classes.title}>Companies Management</Typography>
          <Tooltip title="Add Company" placement="right-start">
            <IconButton disableRipple className={classes.icon} onClick={() => setAdd(true)}>
              <AddCircle />
            </IconButton>
          </Tooltip>
        </Container>
        <SearchInput placeholder="Search company..." handleSearch={handleSearch} />
      </Container>
      <Table
        actions={actions}
        columns={columns}
        data={companiesData.loading ? [] : companiesData.items}
        emptyStateMessage="No companies found."
        handleChangePage={handleChangePage}
        isLoading={companiesData.loading}
        pageNumber={companiesData.pageNumber}
        perPage={10}
        total={companiesData.totalItems}
      />
      <ManagementModal
        fields={[
          { id: 'name', label: 'Name' },
          { id: 'address', label: 'Address' },
          { id: 'contactPhoneNumber', label: 'Contact Phone Number' },
          { id: 'contactEmail', label: 'Contact Email' },
        ]}
        initialValues={
          (editData || {
            name: '',
            address: '',
            contactPhoneNumber: '',
            contactEmail: '',
          }) as Record<string, string | number>
        }
        isOpen={!!editData || add}
        isSubmitting={!!companiesData.saving}
        onClose={handleClose}
        onSubmit={onSubmit}
        title={add ? 'Add Company' : 'Edit Company'}
        validationSchema={validationSchema}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={() => setShowMessage(false)}
        open={showMessage}
      >
        <Notification severity={companiesData.error ? 'error' : 'success'}>
          {companiesData.error
            ? typeof companiesData.error === 'string'
              ? companiesData.error
              : 'Something went wrong. Please try again.'
            : 'The company was saved.'}
        </Notification>
      </Snackbar>
    </Container>
  );
};

export default Companies;
